import axios from "axios";
import authService from "./authService";
import { showError, showAuthError } from "@/store/errorStore";

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Setup axios interceptors for JWT authentication
 * This should be called once when the app initializes
 */
export const setupAxiosInterceptors = () => {
  // Request interceptor - Add JWT token to all requests
  axios.interceptors.request.use(
    (config) => {
      const token = authService.getToken();

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - Handle 401 errors and token refresh
  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Handle 401 Unauthorized
      if (error.response?.status === 401) {
        // If request already retried, show auth error and logout
        if (originalRequest._retry) {
          showAuthError(() => {
            authService.logout();
            window.location.href = "/";
          });
          return Promise.reject(error);
        }

        // If already refreshing, queue this request
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return axios(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        return new Promise((resolve, reject) => {
          // Try to get a fresh user info (this validates the token)
          authService
            .getCurrentUser()
            .then(() => {
              const token = authService.getToken();
              if (token) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                processQueue(null, token);
                resolve(axios(originalRequest));
              } else {
                throw new Error("No token after refresh");
              }
            })
            .catch((err) => {
              processQueue(err, null);

              // Show auth error modal
              showAuthError(() => {
                authService.logout();
                window.location.href = "/";
              });

              reject(err);
            })
            .finally(() => {
              isRefreshing = false;
            });
        });
      }

      // Handle other HTTP errors
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        let errorMessage = "Ocorreu um erro ao processar sua solicitação.";
        let errorTitle = "Erro";

        // Extract error message from backend response
        if (data?.message) {
          errorMessage = data.message;
        } else if (data?.error) {
          errorMessage = data.error;
        } else if (data?.msg) {
          errorMessage = data.msg;
        }

        // Customize message based on status code
        switch (status) {
          case 400:
            errorTitle = "Requisição Inválida";
            break;
          case 403:
            errorTitle = "Acesso Negado";
            errorMessage = "Você não tem permissão para realizar esta ação.";
            break;
          case 404:
            errorTitle = "Não Encontrado";
            errorMessage = "O recurso solicitado não foi encontrado.";
            break;
          case 500:
            errorTitle = "Erro no Servidor";
            errorMessage =
              "Ocorreu um erro interno no servidor. Tente novamente mais tarde.";
            break;
          case 503:
            errorTitle = "Serviço Indisponível";
            errorMessage =
              "O serviço está temporariamente indisponível. Tente novamente em alguns instantes.";
            break;
        }

        // Show error modal (except for 401 which is handled above)
        if (status !== 401) {
          showError(errorMessage, errorTitle);
        }
      } else if (error.request) {
        // Network error - no response received
        showError(
          "Não foi possível conectar ao servidor. Verifique sua conexão com a internet.",
          "Erro de Conexão"
        );
      }

      return Promise.reject(error);
    }
  );
};

/**
 * Clear all axios interceptors (useful for testing or cleanup)
 */
export const clearAxiosInterceptors = () => {
  axios.interceptors.request.handlers = [];
  axios.interceptors.response.handlers = [];
};

export default setupAxiosInterceptors;
